import React, { useEffect, useState } from "react";
import {
    Box, Button, Card, CardContent, Chip, Grid, Paper, TextField, Typography, CardHeader,
    CardActions,
} from "@mui/material";
import { useAuth, useLoading, useSnackbar, useNavigation } from "../../Context/Contexts";
import { SystemLogViewModel } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ResponsivePagination from "../../components/ResponsivePagination";
const SystemLogListPage: React.FC = () => {
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { navigate } = useNavigation();
    const { t } = useTranslation();
    const [items, setItems] = useState<SystemLogViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "", filters: [] });
    const [filter, setFilter] = useState("");
    const [filterProperty, setFilterProperty] = useState("");
    const isBooleanProperty = filterProperty === "ext_Error" || filterProperty === "ext_ContainsJsons";

    useEffect(() => {
        if (isBooleanProperty) setFilter("true");
    }, [isBooleanProperty]);

    const fetchLogs = async () => {
        showLoading();
        try {
            const res = await authService.post<{ items: SystemLogViewModel[]; count: number }>("/systemLogApi/GetSystemLogs", query);
            setItems(res.items);
            setTotalCount(res.count);
        } catch (error: any) {
            showSnackbar(`${t("common.failedToFetchData")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    useEffect(() => { fetchLogs(); }, [query]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }
        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f => f.property === newFilter.property && f.value === newFilter.value);
            if (!isDuplicate) {
                const newFilters = [...(query.filters ?? []), newFilter];
                setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
            }
        } else {
            setQuery({ ...query, page: 0, filter });
        }
        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const openErrorInNewTab = async (id: number) => {
        showLoading();
        try {
            const blob = await authService.get<Blob>(`/systemLogApi/GetErrorFile?id=${id}`, "blob");
            const url = URL.createObjectURL(blob);
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!w) {
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            showSnackbar(`${t("common.openFailed") ?? "Open failed"}: ${e.response?.data ?? e.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    const openJsonInNewTab = async (id: number) => {
        showLoading();
        try {
            const blob = await authService.get<Blob>(`/systemLogApi/GetJsonFile?id=${id}`, "blob");
            const url = URL.createObjectURL(blob);
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!w) {
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            showSnackbar(`${t("common.openFailed") ?? "Open failed"}: ${e.response?.data ?? e.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    return (
        <CenteredListCard title={t("common.systemLog")}>
       
                <Box sx={{ marginBottom: 2 }}>
                    <Button variant="contained" onClick={() => navigate({ path: "/admin/systemlog/archive" })}>
                        {t("common.archive")}
                    </Button>
                </Box>
                <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={5} lg={2}>
                            <TextField
                                select
                                label={t("common.field")}
                                value={filterProperty}
                                onChange={(e) => setFilterProperty(e.target.value)}
                                SelectProps={{ native: true }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="">{t("common.default")}</option>
                                <option value="ext_SystemDescription">{t("common.description")}</option>
                                <option value="ext_Key">{t("common.key")}</option>
                                <option value="ext_Id">Id</option>
                                <option value="ext_Method">{t("common.method")}</option>
                                <option value="ext_Error">{t("common.error")}</option>
                                <option value="ext_ContainsJsons">JSON</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={7} lg={7}>
                            {isBooleanProperty ? (
                                <TextField
                                    select
                                    label={t("common.value")}
                                    variant="outlined"
                                    fullWidth
                                    onChange={(e) => setFilter(e.target.value)}
                                    value={filter}
                                    disabled={isLoading}
                                    SelectProps={{ native: true }}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                >
                                    <option value="true">{t("common.yes")}</option>
                                    <option value="false">{t("common.no")}</option>
                                </TextField>
                            ) : (
                                <TextField
                                    label={t("common.search")}
                                    variant="outlined"
                                    fullWidth
                                    onChange={(e) => setFilter(e.target.value)}
                                    value={filter}
                                    disabled={isLoading}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Button variant="contained" color="primary" type="submit" disabled={isLoading}>
                                {t("common.search")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {query.filters?.map((f, index) => (
                        <Chip
                            key={index}
                            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                            onDelete={() => {
                                const newFilters = query.filters?.filter((_, i) => i !== index);
                                setQuery({ ...query, filters: newFilters, page: 0 });
                            }}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {items.map((item: any) => {
                        const hasError = !!item.error;
                        const hasJson = !!item.containsJsons;

                        return (
                            <Grid item xs={12} key={item.id}>
                                <Card
                                    variant="outlined"
                                    sx={(theme) => ({
                                        backgroundColor: hasError
                                            ? alpha(theme.palette.error.light, 0.18) // pink-ish
                                            : hasJson
                                                ? alpha(theme.palette.info.light, 0.18) // light blue
                                                : theme.palette.background.paper,
                                        borderLeft: `6px solid ${hasError
                                            ? theme.palette.error.main
                                            : hasJson
                                                ? theme.palette.info.main
                                                : theme.palette.divider
                                            }`,
                                    })}
                                >
                                    <CardHeader
                                        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
                                        title={item.dateTime ? new Date(item.dateTime).toLocaleString() : undefined}
                                        subheader={
                                            `Id: ${item.id}`
                                        }
                                    />

                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "150px 1fr",
                                                rowGap: 0.5,
                                                columnGap: 2,
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {t("common.description")}:
                                            </Typography>
                                            <Typography variant="body2">
                                                {item.systemDescription || ""}
                                            </Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                {t("common.method")}:
                                            </Typography>
                                            <Typography variant="body2">{item.method || ""}</Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                {t("common.key")}:
                                            </Typography>
                                            <Typography variant="body2">{item.key || ""}</Typography>
                                        </Box>

                                        {item.message && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {t("common.message")}:
                                                </Typography>
                                                <Box
                                                    component="pre"
                                                    sx={{
                                                        m: 0,
                                                        p: 1,
                                                        borderRadius: 1,
                                                        border: "1px dashed",
                                                        borderColor: "divider",
                                                        whiteSpace: "pre-wrap",
                                                        fontFamily:
                                                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {item.message}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>

                                    {(hasError || hasJson) && (
                                        <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                                            {hasError && (
                                                <Button
                                                    component="a"
                                                    href="#"
                                                    onClick={(e) => { e.preventDefault(); openErrorInNewTab(item.id); }}
                                                    size="small"
                                                    variant="contained"
                                                    color="error"
                                                    startIcon={<PriorityHighRoundedIcon />}
                                                    endIcon={<DownloadRoundedIcon />}
                                                >
                                                    {t("common.error")}
                                                </Button>
                                            )}

                                            {hasJson && (
                                                <Button
                                                    component="a"
                                                    href="#"
                                                    onClick={(e) => { e.preventDefault(); openJsonInNewTab(item.id); }}
                                                    size="small"
                                                    variant="contained"
                                                    color="info"
                                                    startIcon={<InfoOutlinedIcon />}
                                                    endIcon={<DownloadRoundedIcon />}
                                                >
                                                    {t("common.info") ?? "Info"}
                                                </Button>
                                            )}
                                        </CardActions>
                                    )}
                                </Card>
                            </Grid>
                        );
                    })}

                    {!isLoading && items.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                        </Grid>
                    )}
                </Grid>
                <ResponsivePagination

                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalCount}

                    onPageChange={handlePageChange}

                />
        
        </CenteredListCard>
    );
};

export default SystemLogListPage;